import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Tabs, getColor, Icons, Typography } from "fds";
import "./index.scss";
import dayjs from "dayjs";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import utilsCommon from "../../shared/utils/common";
import {
  DocumentListReq,
  DocumentListResp,
  favoriteReq,
} from "../../types/files";
import { meetingDTO, ZoomRespondDto } from "../../types/meeting";
import DocumentBottom from "../../components/DocumentBottom";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import MeetingBottom from "../../components/MeetingBottom";
import { Workgroup } from "../../types/workgroup";
import DateDropdown from "../../components/DateDropdown";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { Trans, useTranslation } from "react-i18next";
import WorkgroupCheckboxDropdown from "../../components/WorkgroupCheckboxDropdown";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

const Favorite = () => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  const INIT_MEETING_FILTER = {
    contentPos: 0,
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().valueOf(),
    },
    meetingPos: 0,
    order: "datedesc",
    page: { meeting: 0, content: 0 },
    resultSize: 30,
    ver: "1.0",
    workgroupIds: [],
  } as favoriteReq;
  const INIT_CONTENT_FILTER = {
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().valueOf(),
    },
    folderCode: "",
    masterUserId: "",
    order: "datedesc",
    page: 0,
    readType: "favorit",
    search: "",
    size: 50,
    tagCodes:
      loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
        ? ["NONEWORKGROUP"]
        : [],
  };
  /** hook 정리 */
  const { t } = useTranslation();
  const { workgroupState, getWorkgroupSimpleList, getFavoriteMeetingList } =
    useWorkgroupStore();
  const { documentState, getContentList } = useDocumentStore();
  const checkboxGroup = useCheckboxGroup();

  /** State 정리 */
  const [workgroups, changeWorkgroups] = useState<Workgroup[]>([]);
  const [meetingList, changeMeetingList] = useState<meetingDTO[]>([]);
  const [meetingInfo, changeMeetingInfo] = useState<ZoomRespondDto>();
  const [docInfo, changeDocInfo] = useState<DocumentListResp>();
  const [docFilter, changeDocFilters] =
    useState<DocumentListReq>(INIT_CONTENT_FILTER);
  const [meetingFilter, changeMeetingFilters] =
    useState<favoriteReq>(INIT_MEETING_FILTER);
  const [dateFilter, changeDateFilter] = useState<string>("1month");
  const [tab, changeTab] = useState<number>(0);
  const [buttonClick, setButtonClick] = useState(0);

  /** UseEffect 정리 */
  useEffect(() => {
    getWorkgroupSimpleList({
      isDetail: "Y",
      managerId: "",
      orderBy: "workgroupName",
      orderByType: "ASC",
      pageRange: 1,
      posPage: 1,
      rowRange: 0,
      searchDate: {
        start: 0,
        end: 0,
      },
      status: 1,
      workgroupName: "",
    });
    if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE") {
      changeDocFilters({
        ...docFilter,
        page: 0,
        tagCodes: ["NONEWORKGROUP"],
      });
    }
  }, []);
  useEffect(() => {
    changeWorkgroups(
      workgroupState.simpleWorkgroupList.filter(
        (item: Workgroup) => item.isValidate !== 0
      )
    );
    checkboxGroup.handleAllClick([
      ...workgroupState.simpleWorkgroupList.map(
        (item: Workgroup) => item.workgroupId
      ),
      "NONEWORKGROUP",
    ]);
    if (
      workgroupState.simpleWorkgroupList &&
      workgroupState.simpleWorkgroupList.length > 0
    ) {
      changeDocFilters({
        ...docFilter,
        page: 0,
        tagCodes: [
          ...(workgroupState.simpleWorkgroupList.map(
            (item: Workgroup) => item.workgroupId
          ) ?? []),
          "NONEWORKGROUP",
        ],
      });
    } else {
      changeDocFilters({
        ...docFilter,
        page: 0,
        tagCodes: ["NONEWORKGROUP"],
      });
    }
  }, [workgroupState.simpleWorkgroupList]);
  useEffect(() => {
    changeDocInfo(documentState.docListInfo);
  }, [documentState.docListInfo]);
  useEffect(() => {
    changeMeetingList(workgroupState.favoriteMeetingList);
    changeMeetingInfo(workgroupState.favoriteMeetingInfo);
  }, [workgroupState.favoriteMeetingList]);
  useEffect(() => {
    if (docFilter.tagCodes.length > 0) getContentList(docFilter);
  }, [docFilter]);
  useEffect(() => {
    if (utilsCommon.checkMenuAvailable("zoom") && meetingFilter)
      getFavoriteMeetingList(meetingFilter);
  }, [meetingFilter]);
  useEffect(() => {
    if (tab === 0) changeDocFilters({ ...docFilter, order: "datedesc" });
    else changeMeetingFilters({ ...meetingFilter, order: "datedesc" });
  }, [tab]);

  /** UI: 필터 초기화 상태인지 확인 */
  const isInitFilter = () => {
    return (
      dayjs(INIT_MEETING_FILTER.date.start).diff(
        meetingFilter.date.start,
        "d"
      ) === 0 &&
      dayjs(INIT_MEETING_FILTER.date.end).diff(meetingFilter.date.end, "d") ===
        0 &&
      (meetingFilter.workgroupIds.length === 0 ||
        (tab === 0
          ? meetingFilter.workgroupIds.length ===
            workgroups.length + ["NONEWORKGROUP"].length
          : meetingFilter.workgroupIds.filter(
              (item) => item !== "NONEWORKGROUP"
            ).length === workgroups.length))
    );
  };
  /** 필터 변경 */
  const changeFilterByKey = (
    key: string,
    value: any,
    type?: "content" | "meeting"
  ) => {
    let newValue = value;
    if (key === "order") {
      if (value.includes("_direct")) {
        newValue = value.split("_direct")[0];
      } else {
        if (value === "file" || value === "name") {
          if (meetingFilter.order.includes("date")) {
            newValue = "filedesc";
          } else {
            newValue = meetingFilter.order.includes("desc")
              ? "file"
              : "filedesc";
          }
        } else {
          if (meetingFilter.order.includes("file")) {
            newValue = "datedesc";
          } else {
            newValue = meetingFilter.order === "datedesc" ? "date" : "datedesc";
          }
        }
      }
    } else if (key === "date") {
      if (typeof value !== "object") {
        changeDateFilter(value);
        changeMeetingFilters({
          ...meetingFilter,
          page: { meeting: 0, content: 0 },
          date: {
            start: utilsCommon.getStartPeriod(value),
            end: dayjs().valueOf(),
          },
        });
        changeDocFilters({
          ...docFilter,
          page: 0,
          date: {
            start: utilsCommon.getStartPeriod(value),
            end: dayjs().valueOf(),
          },
        });
      } else {
        changeDateFilter("custom");
        changeDocFilters({
          ...docFilter,
          page: 0,
          date: {
            start: dayjs(value.start).valueOf(),
            end: dayjs(value.end).valueOf(),
          },
        });
        changeMeetingFilters({
          ...meetingFilter,
          page: { meeting: 0, content: 0 },
          date: {
            start: dayjs(value.start).valueOf(),
            end: dayjs(value.end).valueOf(),
          },
        });
      }
      document.getElementById("docTable")?.scrollTo(0, 0);
      return;
    } else if (key === "page") {
      if (type === "meeting") {
        if (meetingInfo && meetingList.length < meetingInfo?.total) {
          changeMeetingFilters({
            ...meetingFilter,
            [key]: newValue,
          });
        }
      } else {
        if (
          docInfo &&
          docInfo?.docs?.doc &&
          docInfo?.docs?.doc.length < docInfo?.total
        )
          changeDocFilters({
            ...docFilter,
            [key]: newValue,
          });
      }
      return;
    } else if (key === "workgroupIds") {
      changeDocFilters({ ...docFilter, tagCodes: newValue, page: 0 });
      changeMeetingFilters({
        ...meetingFilter,
        [key]: newValue,
        page: { meeting: 0, content: 0 },
      });
      document.getElementById("docTable")?.scrollTo(0, 0);
      return;
    }
    document.getElementById("docTable")?.scrollTo(0, 0);
    changeDocFilters({
      ...docFilter,
      [key]: newValue.replace("name", "file"),
      page: 0,
    });
    changeMeetingFilters({
      ...meetingFilter,
      [key]: newValue.replace("file", "name"),
      page: { meeting: 0, content: 0 },
    });
  };
  /** 필터 초기화 기능 */
  const handleInitFilter = () => {
    changeMeetingFilters({
      ...meetingFilter,
      date: {
        start: utilsCommon.getStartPeriod("1month"),
        end: dayjs().valueOf(),
      },
      workgroupIds: [],
      page: { meeting: 0, content: 0 },
    });
    changeDocFilters({
      ...docFilter,
      date: {
        start: utilsCommon.getStartPeriod("1month"),
        end: dayjs().valueOf(),
      },
      page: 0,
      tagCodes: [
        ...workgroupState.simpleWorkgroupList.map(
          (item: Workgroup) => item.workgroupId
        ),
        "NONEWORKGROUP",
      ],
    });
    changeDateFilter("1month");
    checkboxGroup.handleAllClick([
      ...workgroupState.simpleWorkgroupList.map(
        (item: Workgroup) => item.workgroupId
      ),
      "NONEWORKGROUP",
    ]);
  };
  /** UI : 즐겨찾기  상단 필터 */
  const renderFilter = () => {
    return (
      <Box classes="fav-filter">
        <Button
          leadingIcon={<Icons variant="line" label="redoLeft" />}
          btnStyle="icon"
          size={"sm"}
          onClick={handleInitFilter}
          state={isInitFilter() ? "disable" : "default"}
        />
        {loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" && (
          <WorkgroupCheckboxDropdown
            buttonClick={buttonClick}
            meetingFilter={meetingFilter}
            workgroups={workgroups}
            tab={tab}
            changeFilterByKey={changeFilterByKey}
            setButtonClick={setButtonClick}
          />
        )}
        <DateDropdown
          filter={meetingFilter}
          buttonClick={buttonClick}
          dateFilter={dateFilter}
          setButtonClick={setButtonClick}
          initValue={"1month"}
          changeFilterByKey={changeFilterByKey}
        />
      </Box>
    );
  };
  /** UI : 즐겨찾기 파일 하단 리스트 */
  const renderFavDoc = () => {
    return (
      <DocumentBottom
        filter={meetingFilter}
        docInfo={docInfo}
        changeFilterByKey={changeFilterByKey}
        height={"calc(100% - 200px)"}
        cardTableHeight={"calc(100vh - 373px)"}
        endReached={() =>
          changeFilterByKey(
            "page",
            (meetingFilter?.page.content ?? 0) + 1,
            "content"
          )
        }
        handleInitFilter={handleInitFilter}
        isInitFilter={isInitFilter()}
      />
    );
  };
  /** UI : 즐겨찾기 화상 회의 하단 리스트 */
  const renderFavMeeting = () => {
    return (
      <Box classes="fav-body">
        <Box height={32}>
          <Typography type="subtitle2">
            <Trans
              i18nKey="meeting.withCount"
              values={{ count: meetingInfo?.total ?? 0 }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          </Typography>
        </Box>
        <MeetingBottom
          isInitFilter={
            dateFilter === "1month" &&
            checkboxGroup.selected.length === workgroups.length + 1
          }
          handleInitFilter={handleInitFilter}
          changeFilterByKey={changeFilterByKey}
          meetingList={meetingList}
          height={"calc(100% - 32px)"}
          filter={meetingFilter}
          page={"favorite"}
          handleRefreshMeetingList={() => getFavoriteMeetingList(meetingFilter)}
        />
      </Box>
    );
  };

  return (
    <Box width={"100%"} height={"100%"} classes={"fav body"}>
      <Box height={96} classes={"layout-title"}>
        <Typography type="h1">{t("bookmark.title")}</Typography>
      </Box>
      <Tabs
        type={"leftFull"}
        selectTab={(tab) => changeTab(tab)}
        selected={tab}
        size={"lg"}
        paddingLeft={32}
        tabs={[t("common.file"), t("common.meetings")].map((item, index) => {
          return {
            text: item,
            color: "default",
            isNew: false,
            isHidden:
              (utilsCommon.checkMenuAvailable("zoom") === false ||
                loginDto?.ecoSystemLicenseStats?.featureAccessType ===
                  "LITE") &&
              index === 1,
          };
        })}
      />
      {renderFilter()}
      {tab === 0 ? renderFavDoc() : renderFavMeeting()}
    </Box>
  );
};

export default Favorite;
